import { useEffect, useRef, useState } from 'react';

const HeroSection = () => {
  const [visible, setVisible] = useState(false);
  const particlesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    size: Math.random() * 4 + 2,
    delay: Math.random() * 3,
    color: ['#00d4ff', '#39ff14', '#b026ff', '#ff073a'][Math.floor(Math.random() * 4)],
  }));

  return (
    <section className="relative min-h-screen w-full flex flex-col items-center justify-center overflow-hidden">
      {/* Full Background — dark base */}
      <div className="absolute inset-0 bg-[#050810]" />

      {/* Animated Scan Line */}
      <div
        className="absolute left-0 right-0 h-px opacity-20 pointer-events-none z-10"
        style={{
          background: 'linear-gradient(90deg, transparent, #39ff14, transparent)',
          animation: 'scan-line 4s linear infinite',
        }}
      />

      {/* Neon Grid Lines */}
      <div
        className="absolute inset-0 pointer-events-none opacity-5 z-0"
        style={{
          backgroundImage: `linear-gradient(rgba(0,212,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.5) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Floating Particles */}
      <div ref={particlesRef} className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        {particles.map((p) => (
          <div
            key={p.id}
            className="absolute rounded-full animate-float"
            style={{
              left: p.left,
              top: p.top,
              width: p.size,
              height: p.size,
              backgroundColor: p.color,
              boxShadow: `0 0 ${p.size * 4}px ${p.color}`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${3 + Math.random() * 3}s`,
            }}
          />
        ))}
      </div>

      {/* Split Layout Container */}
      <div className="relative z-10 w-full min-h-screen flex flex-col lg:flex-row items-stretch">

        {/* LEFT — Text Content */}
        <div
          className={`flex-1 flex flex-col justify-center px-8 md:px-12 lg:px-16 py-24 lg:py-0 transition-all duration-1000 ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
          }`}
        >
          {/* Badge */}
          <div
            className="mb-6 self-start px-4 py-1.5 rounded-full border text-xs font-rajdhani font-bold tracking-widest uppercase"
            style={{ borderColor: '#39ff14', color: '#39ff14', boxShadow: '0 0 15px rgba(57,255,20,0.3)' }}
          >
            Tampa, Florida&apos;s #1 Mobile LED Advertising
          </div>

          {/* Main Headline */}
          <h1 className="font-orbitron font-black leading-none mb-4">
            <span
              className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
              style={{ color: '#00d4ff', textShadow: '0 0 20px #00d4ff, 0 0 60px rgba(0,212,255,0.5)' }}
            >
              THE MOST KNOWN
            </span>
            <span
              className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl my-2"
              style={{ color: '#ffffff', textShadow: '0 0 30px rgba(255,255,255,0.3)' }}
            >
              ALWAYS BEATS
            </span>
            <span
              className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
              style={{ color: '#39ff14', textShadow: '0 0 20px #39ff14, 0 0 60px rgba(57,255,20,0.5)', animation: 'glow-pulse 2s ease-in-out infinite' }}
            >
              THE BEST
            </span>
          </h1>

          {/* Subheadline */}
          <p className="mt-5 text-base sm:text-lg md:text-xl font-rajdhani font-medium text-gray-300 tracking-wide max-w-xl">
            We take your brand to the streets of Tampa — reaching{' '}
            <strong style={{ color: '#00d4ff' }}>thousands of eyes daily</strong> with unstoppable mobile LED advertising.
          </p>

          {/* Stats Row */}
          <div className="flex flex-wrap gap-8 mt-8 mb-10">
            {[
              { value: '2000+', label: 'Daily Impressions' },
              { value: '100%', label: 'Brand Visibility' },
              { value: '24/7', label: 'On The Streets' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div
                  className="font-orbitron font-bold text-2xl md:text-3xl"
                  style={{ color: '#39ff14', textShadow: '0 0 15px #39ff14' }}
                >
                  {stat.value}
                </div>
                <div className="text-xs text-gray-400 font-rajdhani tracking-widest uppercase mt-1">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <a
              href="#contact"
              className="relative group px-8 py-4 rounded-full font-rajdhani font-bold tracking-widest uppercase text-base text-black transition-all duration-300 hover:scale-105 whitespace-nowrap overflow-hidden"
              style={{ backgroundColor: '#39ff14', boxShadow: '0 0 30px rgba(57,255,20,0.6)' }}
            >
              <span className="relative z-10">Start Your Campaign</span>
              <div
                className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: 'radial-gradient(circle at center, rgba(57,255,20,0.3), transparent 70%)' }}
              />
            </a>
            <a
              href="#pricing"
              className="px-8 py-4 rounded-full font-rajdhani font-bold tracking-widest uppercase text-base transition-all duration-300 hover:scale-105 whitespace-nowrap"
              style={{
                color: '#00d4ff',
                border: '2px solid #00d4ff',
                boxShadow: '0 0 20px rgba(0,212,255,0.3)',
                backgroundColor: 'transparent',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'rgba(0,212,255,0.1)';
                (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 0 40px rgba(0,212,255,0.5)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
                (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 0 20px rgba(0,212,255,0.3)';
              }}
            >
              View Pricing
            </a>
          </div>
        </div>

        {/* RIGHT — Truck Image (fills full right half) */}
        <div
          className={`relative flex-1 min-h-[50vh] lg:min-h-screen transition-all duration-1000 delay-200 ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
          }`}
        >
          {/* Glow edge on the left side of the image panel */}
          <div
            className="absolute left-0 top-0 bottom-0 w-1 z-10"
            style={{ background: 'linear-gradient(to bottom, #b026ff, #00d4ff, #39ff14)', boxShadow: '0 0 30px #00d4ff' }}
          />

          <img
            src="https://public.readdy.ai/ai/img_res/edited_4d0b1bfc8e632805168aa5c36ac09e61_7b0fad18.jpg"
            alt="MartLed LED truck Tampa"
            className="w-full h-full object-cover object-center"
          />

          {/* Subtle dark gradient on the left edge to blend with text panel */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#050810]/80 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050810]/60 via-transparent to-transparent" />

          {/* Floating badge over image */}
          <div
            className="absolute top-8 right-8 px-4 py-2 rounded-full text-xs font-rajdhani font-bold backdrop-blur-sm"
            style={{ backgroundColor: 'rgba(0,0,0,0.75)', color: '#39ff14', border: '1px solid #39ff14', boxShadow: '0 0 15px rgba(57,255,20,0.4)' }}
          >
            <i className="ri-eye-line mr-1" /> 2000+ Daily Views
          </div>
          <div
            className="absolute bottom-8 right-8 px-4 py-2 rounded-full text-xs font-rajdhani font-bold backdrop-blur-sm"
            style={{ backgroundColor: 'rgba(0,0,0,0.75)', color: '#00d4ff', border: '1px solid #00d4ff', boxShadow: '0 0 15px rgba(0,212,255,0.4)' }}
          >
            <i className="ri-map-pin-line mr-1" /> Tampa, FL
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-8 lg:left-16 flex flex-col items-center gap-2 animate-bounce z-20">
        <span className="text-xs font-rajdhani tracking-widest uppercase text-gray-500">Scroll</span>
        <i className="ri-arrow-down-line text-xl" style={{ color: '#39ff14', textShadow: '0 0 10px #39ff14' }} />
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-dark-base to-transparent z-10 pointer-events-none" />
    </section>
  );
};

export default HeroSection;
